import { window } from "global";
import { useState, useEffect } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { HeaderBullet } from "@ugg/shared/components/common/HeaderBullet";

import {
  useGetOauthConnectionsQuery,
  useInitiateOauthLinkLazyQuery,
  useUnlinkOauthConnectionMutation,
} from "@ugitgud/legos/queries/auth";
import { SvgBrandsGoogle } from "@ugitgud/legos/ui/icon/SVG/BrandsGoogle";
import { SvgBrandsRiot } from "@ugitgud/legos/ui/icon/SVG/BrandsRiot";
import { SvgLolDiscordIcon } from "@ugitgud/legos/ui/icon/SVG/LolDiscordIcon";

// section-content background from settings.scss
const SECTION_BG = "#191937";

const PROVIDERS = [
  {
    key: "riot",
    label: "Riot Games",
    bgColor: "#eb0029",
    logo: <SvgBrandsRiot style={{ width: 22, height: 22 }} color="#ffffff" />,
  },
  {
    key: "google",
    label: "Google",
    bgColor: "#ffffff",
    logo: <SvgBrandsGoogle style={{ width: 22, height: 22 }} />,
  },
  {
    key: "discord",
    label: "Discord",
    bgColor: "#5865f2",
    logo: <SvgLolDiscordIcon style={{ width: 22, height: 22, color: "#ffffff" }} />,
  },
];

const LinkedAccounts = ({ setRedirectingProvider }) => {
  const location = useLocation();
  const history = useHistory();
  const [providerLinkError] = useState(location.state?.providerLinkError || null);
  const [unlinkingProvider, setUnlinkingProvider] = useState(null);

  const { data, loading, refetch } = useGetOauthConnectionsQuery();
  const connections = data?.getOauthConnections || [];

  const [initiateOauthLink, linkState] = useInitiateOauthLinkLazyQuery();
  const [unlinkOauthConnection] = useUnlinkOauthConnectionMutation();

  useEffect(() => {
    refetch();
  }, []);

  useEffect(() => {
    if (linkState.data?.initiateOauthLink?.url) {
      window.location.href = linkState.data.initiateOauthLink.url;
    }
  }, [linkState.data]);

  useEffect(() => {
    if (location.hash === "#linked-accounts") {
      const timer = setTimeout(() => {
        const el = document.getElementById("linked-accounts");
        if (el) {
          const pageContent = document.getElementById("page-content");
          const mastheadHeight = pageContent ? parseInt(getComputedStyle(pageContent).marginTop) || 0 : 0;
          const top = el.getBoundingClientRect().top + window.scrollY - mastheadHeight;
          window.scrollTo({ top, behavior: "smooth" });
        }
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [location.hash]);

  useEffect(() => {
    if (location.state?.providerLinkError) {
      const { providerLinkError: _, ...rest } = location.state;
      history.replace({ pathname: location.pathname, search: location.search, hash: location.hash, state: rest });
    }
  }, []);

  const handleLink = (providerKey) => {
    setRedirectingProvider(providerKey);
    initiateOauthLink({ variables: { providerName: providerKey, redirectLocal: process.env.NODE_ENV === "development" } });
  };

  const handleUnlink = (connection, providerKey) => {
    setUnlinkingProvider(providerKey);
    unlinkOauthConnection({
      variables: { connectionUuid: connection.connectionUuid },
      onCompleted: () => {
        refetch();
        setUnlinkingProvider(null);
      },
      onError: () => {
        setUnlinkingProvider(null);
      },
    });
  };

  return (
    <div id="linked-accounts" className="settings-section linked-accounts">
      <div className="section-content">
        <HeaderBullet className="mb-[16px]">Linked Accounts</HeaderBullet>
        <div className="h-[1px] w-full bg-purple-100" />

        <div className="flex flex-col">
          {PROVIDERS.map((provider, index) => {
            const connection = !loading ? connections.find((c) => c.providerName?.toLowerCase() === provider.key) : null;
            const isLinked = !!connection;
            const isUnlinking = unlinkingProvider === provider.key;
            const linkError =
              !isLinked && providerLinkError?.providerName?.toLowerCase() === provider.key ? providerLinkError.message : null;

            return (
              <div key={provider.key}>
                <div className="flex items-center py-[16px]">
                  {/* Logo box with status dot */}
                  <div className="relative flex-shrink-0 mr-[14px]">
                    <div
                      className="w-[36px] h-[36px] rounded-[4px] flex items-center justify-center"
                      style={{ backgroundColor: provider.bgColor }}
                    >
                      {provider.logo}
                    </div>
                    {/* Status indicator dot */}
                    <div
                      className="absolute w-[13px] h-[13px] rounded-full border-[2px]"
                      style={{
                        bottom: "-5px",
                        right: "-5px",
                        backgroundColor: isLinked ? "#0ade96" : "#383864",
                        borderColor: SECTION_BG,
                      }}
                    />
                  </div>

                  {/* Provider name + username/subtitle */}
                  <div className="flex flex-col flex-1 min-w-0 mr-[12px]">
                    <span className="text-white text-[16px] font-semibold font-inter leading-[1.3]">{provider.label}</span>
                    {isLinked ? (
                      <span className="text-lavender-50 text-[12px] font-inter truncate">
                        {provider.key === "google"
                          ? connection.providerEmail || connection.providerUserId
                          : connection.providerUsername || connection.providerUserId}
                      </span>
                    ) : linkError ? (
                      <span className="text-accent-red-450 text-[12px] font-inter">{linkError}</span>
                    ) : (
                      <span className="text-lavender-300 text-[12px] font-inter">Not currently linked</span>
                    )}
                  </div>

                  {/* Action button */}
                  {isLinked ? (
                    <button
                      type="button"
                      onClick={() => !isUnlinking && handleUnlink(connection, provider.key)}
                      disabled={isUnlinking}
                      className="text-lavender-50 text-[14px] font-inter underline cursor-pointer bg-transparent border-0 p-0 flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isUnlinking ? "Unlinking..." : `Unlink ${provider.label} Account`}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleLink(provider.key)}
                      className="text-lavender-50 text-[14px] font-inter underline cursor-pointer bg-transparent border-0 p-0 flex-shrink-0"
                    >
                      Link Account
                    </button>
                  )}
                </div>
                {index < PROVIDERS.length - 1 && <div className="h-[1px] w-full bg-purple-100" />}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default LinkedAccounts;
